// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Company, companyFields } from 'src/app/crm/models/company.model';
import { environment } from '../../../environments/environment';
import { forkJoin, from, Observable } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { Contact, contactFields } from 'src/app/crm/models/contact.model';
import { loadAllRequests } from '@fc-core';
import { Router } from '@angular/router';
import { Update } from '@ngrx/entity';

@Injectable({
  providedIn: 'root',
})
export class CrmService {
  constructor(private http: HttpClient) {}

  addCompany(company: Company): Observable<Company> {
    const payload: Company = Object.assign({}, company);
    delete payload.photo;
    if (payload?.website?.length === 8) delete payload.website;

    return this.http
      .post<Company>(environment.apiUrl + 'api/companies/', payload)
      .pipe(
        mergeMap((companyResponse: Company) => {
          if (company.photo)
            return this.addCompanyPhoto(company.photo, companyResponse.id);
          return from([companyResponse]);
        }),
      );
  }

  updateCompany(company: Company, id: number): Observable<Company> {
    const payload: Company = Object.assign({}, company);
    delete payload.photo;
    if (payload?.website?.length === 8) delete payload.website;
    return this.http
      .patch<Company>(environment.apiUrl + `api/companies/${id}/`, payload)
      .pipe(
        mergeMap((companyResponse: Company) => {
          if (company.photo)
            return this.addCompanyPhoto(company.photo, companyResponse.id);
          return from([companyResponse]);
        }),
      );
  }

  getCompanyList(): Observable<{ list: Company[]; count: number }> {
    const params = new HttpParams().set('fields', companyFields.join(','));
    return loadAllRequests(
      `${environment.apiUrl}api/companies/`,
      this.http,
      params,
    );
  }

  deleteCompany(companyId: string): Observable<Company> {
    return this.http.delete<Company>(
      environment.apiUrl + `api/companies/${companyId}/`,
    );
  }

  getContactList(): Observable<{ list: Contact[]; count: number }> {
    const params = new HttpParams().set('fields', contactFields.join(','));
    return loadAllRequests(
      `${environment.apiUrl}api/contacts/`,
      this.http,
      params,
    );
  }

  updateContacts(updates: Update<Contact>[]): Observable<Contact[]> {
    return forkJoin(
      updates.map((update) => this.patchContact(update.changes, +update.id)),
    );
  }

  patchContact(changes: Partial<Contact>, id: number): Observable<Contact> {
    return this.http.patch<Contact>(
      environment.apiUrl + `api/contacts/${id}/`,
      changes,
    );
  }

  updateContact(contact: Contact, id: number): Observable<Contact> {
    const payload: any = Object.assign({}, contact);
    delete payload.photo;
    return this.http
      .patch<Contact>(environment.apiUrl + `api/contacts/${id}/`, payload)
      .pipe(
        mergeMap((contactResponse: Contact) => {
          if (contact.photo)
            return this.addContactPhoto(contact.photo, contactResponse.id);
          return from([contactResponse]);
        }),
      );
  }

  addContact(contact: Contact): Observable<Contact> {
    const payload: any = Object.assign({}, contact);
    delete payload.photo;
    return this.http
      .post<Contact>(environment.apiUrl + 'api/contacts/', payload)
      .pipe(
        mergeMap((contactResponse: Contact) => {
          if (contact.photo)
            return this.addContactPhoto(contact.photo, contactResponse.id);
          return from([contactResponse]);
        }),
      );
  }

  addContactPhoto(photo: any, id: number): Observable<Contact> {
    const formData = new FormData();
    formData.append('photo', photo);
    return this.http.patch<Contact>(
      environment.apiUrl + `api/contacts/${id}/`,
      formData,
    );
  }

  addCompanyPhoto(photo: any, id: number): Observable<Company> {
    const formData = new FormData();
    formData.append('photo', photo);
    return this.http.patch<Company>(
      environment.apiUrl + `api/companies/${id}/`,
      formData,
    );
  }

  deleteContact(contactId: string): Observable<Contact> {
    return this.http.delete<Contact>(
      environment.apiUrl + `api/contacts/${contactId}/`,
    );
  }
}

export function navigateToDefaultCRMRoute<T extends { id?: number }>(
  items: T[],
  routeType: string,
  router: Router,
  isMobile: boolean = false,
): T[] {
  if (
    items?.length > 0 &&
    router.url.split('/').pop() === routeType &&
    !isMobile
  ) {
    router.navigate(['/crm/main/', routeType, items[0].id], {
      replaceUrl: true,
    });
  }
  return items;
}
